import { call, put, takeLatest, select, take } from 'redux-saga/effects';
import * as API from './api';
import { map, first } from 'lodash';
import {
  PAYPAL_CONNECT_ACTION_TYPES,
  getSellerStatusSucceedAction,
  getSellerStatusAction,
  fetchAllPaypalConnectAccountsSucceed,
  paypalConnectFailAction,
  paypalFetchAllPaymentsSucceeded,
  disconnectPaypalAccountSucceedAction,
  getOrderDetailsSucceedAction,
  getCaptureDetailsSucceedAction,
} from './reducer';
import {
  DONATIONS_ACTION_TYPES,
  fetchAllDonationsSuccess,
} from '../donation/DonationOperations';
// import { startListeningForDonations, ACTION_TYPES as DONATION_ACTION_TYPES } from '../donation/DonationOperations'
import { database } from '../../firebase';
import * as logger from '../../helpers/logger';
enum ACCOUNT_STATUS {
  CREATED = 'created',
  DISCONNECTED = 'disconnected',
  CONNECTED = 'connected',
  COMPLETED = 'completed'
}

const getAuthState = (state: any) => state.auth;

/**
 * Sets or clears the paypalAccount field in the charities object.
 * This field is used to determine if PayPal can be used for donations in the widget
 * If set, the value matches the key in paypalConnectedAccounts
 * @param authId user authorising the PayPal account
 * @param value paypalConnectedAccounts key or null
 */
async function setPaypalAccountInCharity(authId: string, value: string | null) {
  // retrieve the charity associated with the user
  const ckval = await database
    .ref(`/users/${authId}/charityKey`)
    .once('value');
  const charityID = ckval.val();

  if (charityID) {
    // update the charities object
    await database
      .ref(`/charities/${charityID}`)
      .child('paypalAccount')
      .set(value);
  }
}

// Create or update paypal account in firebase
const createPaypalConnectAccount = async (item: any) => {
  try {
    const { authId, ...rest } = item;
    const connectAccRef = database.ref('/paypalConnectedAccounts');
    await connectAccRef.child(authId).set({ ...rest });
    return true;
  } catch (error) {
    return false;
  }
};

// Update infor Paypal account in firebase

const updatePaypalConnectAccount = async (item: any) => {
  const { authId, ...rest } = item;
  const connectAccRef = database.ref('/paypalConnectedAccounts');
  const snapshot = await connectAccRef.child(authId).once('value');
  const current = snapshot.val();
  const updated = {
    authId,
    ...current,
    ...rest
  };
  const result = await createPaypalConnectAccount(updated);

  // if the is_connect value has changed, update the value in the charities object
  if (!current || (current.is_connect !== updated.is_connect)) {
    await setPaypalAccountInCharity(authId, updated.is_connect ? authId : null);
  }

  return result;
};

async function deletePaypalConnectAccount(authId: string) {
  const connectAccRef = database.ref('/paypalConnectedAccounts');
  await connectAccRef.child(authId).remove();
  await setPaypalAccountInCharity(authId, null);
}

// Disconnect paypal account
const disconnectPaypalAccount = function*() {
  try {
    const auth = yield select(getAuthState);
    yield call(deletePaypalConnectAccount, auth.uid);

    const data = yield call(fetchPaypalConnectAccounts);
    if (data) {
      yield put(fetchAllPaypalConnectAccountsSucceed(data));
    }

    yield put(disconnectPaypalAccountSucceedAction());
      // payload()
  } catch (error) {
    logger.error(error);
  }
};

// Fetch infor paypal account from firebase

const fetchPaypalConnectAccounts = async () => {
  const connectAccRef = database.ref('/paypalConnectedAccounts');
  const snapshot = await connectAccRef.once('value');
  const accounts = snapshot.val();
  const allUser = {};
  await Promise.all(
    map(accounts, async (paypalAcc, userKey: string) => {
      const users = database.ref('/users');
      const userSnapshot = await users.child(userKey).once('value');
      allUser[userKey] = {
        ...paypalAcc,
        ...userSnapshot.val()
      };
    })
  );
  return allUser;
};

// Check exists paypal account by Uid
const getCurrentConnectedAccount = async (authId: string) => {
  try {
    const snapshot = await database
      .ref(`/paypalConnectedAccounts/${authId}`)
      .once('value');
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    return null;
  }
};

// Get seller status
const getSellerStatus = function*(action: any) {
  try {
    const data = yield call(API.get_seller_status, action.payload.merchant_id);

    if (data) {
      const auth = yield select(getAuthState);
      const {
        merchant_id,
        payments_receivable,
        primary_email_confirmed,
        oauth_integrations
      } = data;
      const { oauth_third_party = [] } = first(oauth_integrations) || {};
      const { scopes = {} } = first(oauth_third_party) || {};
      const result = yield call(updatePaypalConnectAccount, {
        authId: auth.uid,
        merchant_id,
        payments_receivable,
        primary_email_confirmed,
        scopes,
        is_connect: true,
        status: ACCOUNT_STATUS.CONNECTED,
        timestamp: new Date().toISOString(),
        tracking_id: action.payload.tracking_id
      });

      if (result) {
        const paypal_accounts = yield call(fetchPaypalConnectAccounts);
        if (paypal_accounts) {
          yield put(fetchAllPaypalConnectAccountsSucceed(paypal_accounts));
        }

        yield put(getSellerStatusSucceedAction(result));
      }
    }
  } catch (error) {
    yield put(paypalConnectFailAction(error.message));
  }
};

// Fretch paypal connect accounts
const fetchAllPaypalConnectAccounts = function*() {
  try {
    const data = yield call(fetchPaypalConnectAccounts);
    if (data) {
      yield put(fetchAllPaypalConnectAccountsSucceed(data));
    }
    // return data;
  } catch (error) {
    yield put(paypalConnectFailAction(error.message));
  }
};

// Fetch all payments from firebase
const fetchAllPayments = async () => {
  try {
    const payments = database.ref('/payments');
    const snapshot = await payments.once('value');
    const allPayments = snapshot.val();
    // filter out PayPal donations which were not completed
    for (const paymentKey of Object.keys(allPayments)) {
      const donation = allPayments[paymentKey];
      if (donation.payment_type === 'paypal' && !donation.capture) {
        delete allPayments[paymentKey];
      }
    }
    return allPayments;
  } catch (error) {
    throw new Error(error);
  }
};
const fetchAllMonthlyDonationsRef = async () => {
  try {
    const monthlyDonationsRef = database.ref('/stripeSubscriptions');
    const snapshot = await monthlyDonationsRef.once('value');
    const AllmonthlyDonationsRef = snapshot.val();
    return AllmonthlyDonationsRef;
  } catch (error) {
    throw new Error(error);
  }
};

// Request fetch all payment from firebase
const requestFetchAllPayments = function*() {
  try {
    const data = yield call(fetchAllPayments);
    if (data) {
      yield put(paypalFetchAllPaymentsSucceeded(data));
    }
  } catch (error) {
    yield put(paypalConnectFailAction(error.message));
  }
};

/** GET Order details */
const getOrderDetails = function*(action: any) {
  try {
    const data = yield call(API.get_order_details, action.payload);
    if (data) {
      yield put(getOrderDetailsSucceedAction(data));
    }
  } catch (error) {
    yield put(paypalConnectFailAction(error.message));
  }
};

/**
 * Get capture sagas
 * @param action : object
 */
const getCaptureDetails = function*(action: any) {
  try {
    const data = yield call(API.get_capture_details, action.payload);
    if (data) {
      yield put(getCaptureDetailsSucceedAction(data));
    }
  } catch (error) {
    yield put(paypalConnectFailAction(error.message));
  }
};

const requestDonationsAction = function*() {
  try {
    const oneOffDonations = yield call(fetchAllPayments);
    const monthlyDonations = yield call(fetchAllMonthlyDonationsRef);
    const donations = { ...oneOffDonations, ...monthlyDonations };
    if (donations) {
      yield put(fetchAllDonationsSuccess(donations));
    }
  } catch (error) {
    yield put(paypalConnectFailAction(error.message));
  }
};

export default [
  takeLatest(
    PAYPAL_CONNECT_ACTION_TYPES.FETCH_ALL_PAYMENTS,
    requestFetchAllPayments
  ),
  takeLatest(
    PAYPAL_CONNECT_ACTION_TYPES.GET_SELLER_STATUS_REQUEST,
    getSellerStatus
  ),
  takeLatest(
    PAYPAL_CONNECT_ACTION_TYPES.FETCH_ALL_ACCOUNTS_REQUEST,
    fetchAllPaypalConnectAccounts
  ),
  takeLatest(
    PAYPAL_CONNECT_ACTION_TYPES.DISCONNECT_PAYPAL_ACCOUNT_REQUEST,
    disconnectPaypalAccount
  ),
  takeLatest(
    PAYPAL_CONNECT_ACTION_TYPES.GET_ORDER_DETAILS_REQUEST,
    getOrderDetails
  ),
  takeLatest(
    PAYPAL_CONNECT_ACTION_TYPES.GET_CAPTURE_DETAILS_REQUEST,
    getCaptureDetails
  ),
  takeLatest(
    DONATIONS_ACTION_TYPES.REQUEST_INSERT_CHARITIES,
    requestDonationsAction
  )
];
